import React, { useMemo, useRef } from "react";

import { INextGenDashboardKpi } from "../../services/zeos-api/contracts/Dashboard";
import { formatMetricNumber } from "../../services/helpers/DashboardTileHelpers";
import { DashboardStatusBadge } from "../Dashboard/shared/DashboardStatusBadge";
import { MidFiChip, chipToneFromStatusTone } from "./MidFiChip";
import { COMPARE_MODE_LABELS } from "./NextGenChartControls";

interface IProps {
  kpis: INextGenDashboardKpi[];
  compareMode: string;
  /** "grid": mid-fi tiles, all visible (4019 detail pages). "carousel": three at a time (other tiles). */
  layout?: "carousel" | "grid";
}

const VISIBLE_COUNT = 3;

const formatValue = (value: number, unit: string) => {
  const formatted = formatMetricNumber(value, { unit });
  return unit === "$" ? formatted : `${formatted} ${unit}`.trim();
};

const formatDelta = (kpi: INextGenDashboardKpi) => {
  if (kpi.delta == null) {
    return "N/A";
  }

  const sign = kpi.delta > 0 ? "+" : "";
  const value = formatMetricNumber(kpi.delta);

  if (kpi.deltaPercent == null) {
    return `${sign}${value}`;
  }

  const pct = formatMetricNumber(kpi.deltaPercent);
  return `${sign}${value} (${sign}${pct}%)`;
};

// Semantic, not directional (DV-03 KPI conventions) — "up" is bad for cost, good for savings, so
// color follows the backend-computed sentiment, never the raw trend direction.
const trendColor = (sentiment?: string) => {
  if (sentiment === "positive") {
    return "text-emerald-600";
  }
  if (sentiment === "negative") {
    return "text-rose-600";
  }
  return "text-slate-500";
};

// Mid-fi colors for the same semantic sentiment.
const midFiTrendColor = (sentiment?: string) => {
  if (sentiment === "positive") {
    return "text-mf-energy-ink";
  }
  if (sentiment === "negative") {
    return "text-mf-alert";
  }
  return "text-mf-sub";
};

// The value carries the unit on mid-fi tiles, so drop a trailing "(kWh)" / "($)" from the label.
const midFiLabel = (label: string) => label.replace(/\s*\([^)]*\)\s*$/, "");

// Headline precision for mid-fi tiles: whole numbers once a figure is large enough to read that way.
const midFiNumber = (value: number) =>
  value.toLocaleString("en-US", { maximumFractionDigits: Math.abs(value) >= 100 ? 0 : 1 });

const MidFiValue = ({ kpi }: { kpi: INextGenDashboardKpi }) => {
  if (kpi.status === "pending") {
    return <>—</>;
  }
  if (kpi.unit === "$") {
    return <>{`${kpi.value < 0 ? "−" : ""}$${midFiNumber(Math.abs(kpi.value))}`}</>;
  }
  return (
    <>
      {midFiNumber(kpi.value)}
      {kpi.unit ? (
        <small className="ml-1 text-[13px] font-bold text-mf-sub">{kpi.unit}</small>
      ) : null}
    </>
  );
};

const midFiDelta = (kpi: INextGenDashboardKpi) => {
  if (kpi.delta == null) {
    return null;
  }
  const arrow = kpi.delta > 0 ? "▲" : kpi.delta < 0 ? "▼" : "";
  const sign = kpi.delta > 0 ? "+" : "";
  const amount =
    kpi.deltaPercent != null
      ? `${sign}${midFiNumber(kpi.deltaPercent)}%`
      : `${sign}${midFiNumber(kpi.delta)}`;
  return `${arrow} ${amount}`.trim();
};

const SHORT_BADGE_MAX = 12;

// Full class names so Tailwind keeps them; a 4-KPI page (DR / VPP) fills the row instead of leaving gaps.
const gridColumns = (count: number) => {
  if (count <= 4) {
    return "md:grid-cols-2 xl:grid-cols-4";
  }
  if (count === 5) {
    return "md:grid-cols-3 xl:grid-cols-5";
  }
  return "md:grid-cols-3 xl:grid-cols-6";
};

const MidFiKpiGrid = ({ kpis, compareMode }: IProps) => (
  <div className={`grid grid-cols-2 gap-3.5 ${gridColumns(kpis.length)}`}>
    {kpis.map((kpi) => {
      const delta = midFiDelta(kpi);
      // Short status chips (e.g. "EST.") sit beside the label as in the mockup; longer reasons
      // ("No billing data available") go under the value so they don't crowd the label.
      const badgeChip = kpi.badge ? (
        <MidFiChip label={kpi.badge.label} tone={chipToneFromStatusTone(kpi.badge.tone)} className="text-[9px]" />
      ) : null;
      const inlineBadge = kpi.badge && kpi.badge.label.length <= SHORT_BADGE_MAX;
      return (
        <div
          key={kpi.key}
          data-kpi-card
          className="rounded-xl border border-mf-border bg-mf-card px-[18px] pb-3.5 pt-4 shadow-mf-card"
        >
          <div className="mb-2 flex items-start justify-between gap-1.5 text-[10.5px] font-extrabold uppercase tracking-[.07em] text-mf-faint">
            <span>{midFiLabel(kpi.label)}</span>
            {inlineBadge ? badgeChip : null}
          </div>
          <div className="text-[27px] font-extrabold leading-none tracking-tight text-mf-ink">
            <MidFiValue kpi={kpi} />
          </div>
          {badgeChip && !inlineBadge ? <div className="mt-2">{badgeChip}</div> : null}
          {delta ? (
            <div className="mt-2 text-[11.5px] font-semibold text-mf-sub">
              <span className={`font-extrabold ${midFiTrendColor(kpi.sentiment)}`}>{delta}</span>{" "}
              {(COMPARE_MODE_LABELS[compareMode] ?? compareMode).toLowerCase()}
            </div>
          ) : null}
          {kpi.caption ? (
            <div className="mt-1.5 text-[11.5px] font-semibold leading-snug text-mf-sub">{kpi.caption}</div>
          ) : null}
        </div>
      );
    })}
  </div>
);

export const KpiStrip = ({ kpis, compareMode, layout = "carousel" }: IProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const showControls = kpis.length > VISIBLE_COUNT;

  const cardWidth = useMemo(() => {
    const first = containerRef.current?.querySelector<HTMLElement>("[data-kpi-card]");
    return first?.offsetWidth ?? 0;
  }, [kpis.length]);

  const scrollByCard = (direction: "left" | "right") => {
    const container = containerRef.current;
    if (!container) return;
    const width = cardWidth || container.clientWidth / VISIBLE_COUNT;
    const delta = direction === "left" ? -width : width;
    container.scrollBy({ left: delta, behavior: "smooth" });
  };

  if (kpis.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-600">
        No KPIs available
      </div>
    );
  }

  if (layout === "grid") {
    return <MidFiKpiGrid kpis={kpis} compareMode={compareMode} />;
  }

  return (
    <div className="flex items-center gap-2">
      {showControls ? (
        <button
          type="button"
          className="rounded-full border border-slate-200 bg-white p-2 text-slate-600 shadow-sm hover:text-slate-900"
          onClick={() => scrollByCard("left")}
          aria-label="Scroll KPIs left"
        >
          &larr;
        </button>
      ) : null}
      <div
        ref={containerRef}
        className="flex flex-1 gap-4 overflow-x-auto pb-2"
      >
        {kpis.map((kpi) => (
          <div
            key={kpi.key}
            data-kpi-card
            className="flex-none w-[calc((100%-2rem)/3)] rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div className="text-xs uppercase tracking-wide text-slate-500">
              {kpi.label}
            </div>
            <div className="mt-2 text-2xl font-semibold text-slate-900">
              {kpi.status === "pending"
                ? "—"
                : formatValue(kpi.value, kpi.unit)}
            </div>
            {kpi.caption ? (
              <div className="mt-1 text-xs text-slate-500">{kpi.caption}</div>
            ) : null}
            <div className={`mt-2 text-sm ${trendColor(kpi.sentiment)}`}>
              {formatDelta(kpi)}
              {kpi.delta != null ? (
                <span className="ml-1 text-xs text-slate-400">
                  {COMPARE_MODE_LABELS[compareMode] ?? compareMode}
                </span>
              ) : null}
            </div>
            {kpi.badge ? (
              <div className="mt-2">
                <DashboardStatusBadge
                  tone={kpi.badge.tone}
                  label={kpi.badge.label}
                />
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {showControls ? (
        <button
          type="button"
          className="rounded-full border border-slate-200 bg-white p-2 text-slate-600 shadow-sm hover:text-slate-900"
          onClick={() => scrollByCard("right")}
          aria-label="Scroll KPIs right"
        >
          &rarr;
        </button>
      ) : null}
    </div>
  );
};
